/**
 * Which surface announces a live hub notice (cards t_4677fac1, t_f0ea3681).
 *
 * One hub event reaches every surface the operator has open on that hub — in
 * the desktop shell every tab is its own renderer with its own socket. Before
 * this module, each of them painted a balloon and spoke: two balloons on two
 * surfaces ("a tab, but also the home tab") and two utterances per message.
 * The notice layer now announces on **one** surface — the one the operator is
 * looking at — and the others store the notice and bump their unread count
 * (the badge), which is what they can do without shouting.
 *
 * **The desktop launcher home is not even that one** (card t_f0ea3681): it holds
 * no hub socket at all (`services/launcherSurface.ts`), so it must announce
 * nothing — not a balloon, not the alert tone, not the speech — even while it is
 * the tab the operator is looking at. The two questions are separate functions
 * below (`isOperatorSurface` — the shell vs document argument; `isAnnouncingSurface`
 * — the surface that may actually shout), because a launcher tab usually *wins*
 * the first one.
 *
 * The facts, in the order they are trusted:
 *
 *   1. the desktop shell's own answer for THIS tab — `window.electron.tabActive()`,
 *      pushed by the main process, which is the only side that knows which
 *      WebContentsView is active. A `WebContentsView` cannot answer it itself:
 *      measured on the live dev app, all three tab pages report
 *      `document.visibilityState === 'visible'` at the same time. The signal's
 *      presence is itself the shell's marker (nothing else exposes it), and the
 *      preload answers `true` until the shell says otherwise, so a desktop
 *      surface is never muted by a signal that has not arrived.
 *   2. any other document — a browser tab, a Capacitor WebView — where
 *      `document.visibilityState` *is* a real answer (a background tab is
 *      hidden).
 *
 * A desktop surface whose shell has not answered (`tabActive` undefined: a
 * single-window build without the signal, or the moment before the shell's
 * first push) announces — the fix must never turn a desktop app silent.
 *
 * Pure on purpose: the count this module protects ("one event, one announcement
 * across surfaces") is asserted in `noticeSurface.test.ts` without pinia, a
 * socket or a DOM.
 */
import { onDesktopLauncherHome } from './launcherSurface';

export interface NoticeSurfaceFacts {
  /**
   * The desktop shell's answer for THIS tab; `undefined` = this surface has no
   * shell signal at all (no `window.electron.tabActive`), which is every
   * non-shell surface.
   */
  shellTabActive: boolean | undefined;
  /** `document.visibilityState === 'visible'` for this document. */
  documentVisible: boolean;
  /**
   * This surface is the desktop launcher home (card t_f0ea3681), which holds no
   * hub socket at all — `services/launcherSurface.ts`. It is not an announcing
   * surface whatever the shell or the document say.
   */
  launcherSurface?: boolean;
}

/** Whether THIS surface is the one the operator is looking at. */
export function isOperatorSurface(facts: NoticeSurfaceFacts): boolean {
  if (facts.shellTabActive !== undefined) return facts.shellTabActive;
  return facts.documentVisible;
}

/**
 * Whether THIS surface may announce a live notice: it is the operator's surface,
 * and it is not the desktop launcher home.
 *
 * The two questions are kept apart on purpose. `isOperatorSurface` is the shell
 * vs document argument (card t_4677fac1) and a launcher tab often *wins* it — the
 * owner is looking at the picker — while being the one surface that must stay
 * silent (card t_f0ea3681): it has no hub socket, so a notice reaching it is not
 * a notice the app should paint a balloon for, play a tone for or speak.
 */
export function isAnnouncingSurface(facts: NoticeSurfaceFacts): boolean {
  if (facts.launcherSurface === true) return false;
  return isOperatorSurface(facts);
}

/**
 * What one surface does with a live notice: store it (the balloon is the only
 * thing that is shared-and-once; the list and the unread badge stay per
 * surface), and announce it only on the operator's surface.
 */
export interface NoticeIntake {
  /** Take it into the list and bump the unread badge. */
  store: boolean;
  /** Paint the balloon, play the tone and speak — once per event, in total. */
  announce: boolean;
}

/**
 * The notice layer's whole decision for one incoming notice, with the count as
 * the contract: an event already held (`known`) never announces again — a
 * duplicate delivery of the same notification id must not speak twice — and a
 * surface that must not announce stores but never announces.
 */
export function intakeNotice(input: { known: boolean; announcingSurface: boolean }): NoticeIntake {
  if (input.known) return { store: false, announce: false };
  return { store: true, announce: input.announcingSurface };
}

// ── the live surface ─────────────────────────────────────────────────────────

/** The desktop bridge, as far as this module needs it. */
interface TabActiveBridge {
  tabActive?: () => boolean;
}

function bridge(): TabActiveBridge | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { electron?: TabActiveBridge }).electron;
}

function documentIsVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

/** The facts as they stand right now, for the store's own decision. */
export function noticeSurfaceFacts(): NoticeSurfaceFacts {
  const b = bridge();
  let shellTabActive: boolean | undefined;
  if (typeof b?.tabActive === 'function') {
    try {
      shellTabActive = b.tabActive() === true;
    } catch {
      shellTabActive = undefined;
    }
  }
  return {
    shellTabActive,
    documentVisible: documentIsVisible(),
    // The desktop launcher home (card t_f0ea3681): read from the surface module,
    // which the app root keeps up to date with the route this window is on.
    launcherSurface: onDesktopLauncherHome(),
  };
}

/** Whether the notice THIS surface just received should be announced here. */
export function currentSurfaceAnnounces(): boolean {
  return isAnnouncingSurface(noticeSurfaceFacts());
}
